import * as t from 'io-ts'
import * as tv from '@espoon-voltti/ts-libs/lib/io-ts-types'

import {
  DraftState,
  ReviewState,
  SentState,
  SigningState,
  PaymentWaitState,
  PreReportState,
  RejectedState,
  PaidState,
  PostReportState,
  RecoveryState,
  RecoveredState
} from '@espoon-voltti/nuora-domain/grants'

import {
  DraftNuoraOrgDto,
  OrgDto
} from '@espoon-voltti/nuora-app-common/services/dtos'

import { applicant } from '../applicants'
import { reviewData, sentData, signingData } from './app-partials'
import {
  GeneralGrantFormDataDto,
  ProjectGrantFormDataDto,
  GeneralGrantPaperFormDataDto
} from './form-data'
import {
  approvedDecisionData,
  postReportDecisionData,
  preReportDecisionData,
  preReportOrRejectedDecisionData
} from './decision'
import {
  maybeApprovedPostReportDecisionData,
  maybeApprovedPreReportDecisionData,
  maybeRejectedPreReportDecisionData,
  rejectedPostReportDecisionData
} from './report'

// response: decoders, backend responds with these

export const DraftEventDto = t.type(
  {
    name: t.union([t.string, t.null]),
    location: t.union([t.string, t.null]),
    startDate: t.union([tv.localDateFromString, t.null]),
    endDate: t.union([tv.localDateFromString, t.null])
  },
  'DraftEventDto'
)
export type DraftEventDto = t.TypeOf<typeof DraftEventDto>

export const ProjectGrantDraftAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,

    // applicant
    applicant: t.type({
      org: OrgDto,
      nuoraOrg: DraftNuoraOrgDto
    }),
    form: ProjectGrantFormDataDto,

    // meta
    state: DraftState,
    modifiedAt: tv.dateTimeFromString
  },
  'ProjectGrantDraftAppDto'
)
export type ProjectGrantDraftAppDto = t.TypeOf<typeof ProjectGrantDraftAppDto>

export const GeneralGrantDraftAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,

    // applicant
    applicant: t.type({
      org: OrgDto,
      nuoraOrg: DraftNuoraOrgDto
    }),
    form: GeneralGrantFormDataDto,

    // meta
    state: DraftState,
    modifiedAt: tv.dateTimeFromString
  },
  'GeneralGrantDraftAppDto'
)
export type GeneralGrantDraftAppDto = t.TypeOf<typeof GeneralGrantDraftAppDto>

export const DraftAppDto = t.union([
  GeneralGrantDraftAppDto,
  ProjectGrantDraftAppDto
])
export type DraftAppDto = t.TypeOf<typeof DraftAppDto>

export const SigningAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: SigningState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([ProjectGrantFormDataDto, GeneralGrantFormDataDto]),
    ...signingData
  },
  'SigningAppDto'
)
export type SigningAppDto = t.TypeOf<typeof SigningAppDto>

export const SentAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: SentState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([
      ProjectGrantFormDataDto,
      GeneralGrantFormDataDto,
      GeneralGrantPaperFormDataDto
    ]),
    ...signingData,
    ...sentData
  },
  'SentAppDto'
)
export type SentAppDto = t.TypeOf<typeof SentAppDto>

export const ReviewAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: ReviewState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([
      ProjectGrantFormDataDto,
      GeneralGrantFormDataDto,
      GeneralGrantPaperFormDataDto
    ]),
    ...signingData,
    ...sentData,
    ...reviewData
  },
  'ReviewAppDto'
)
export type ReviewAppDto = t.TypeOf<typeof ReviewAppDto>

export const PaymentWaitAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: PaymentWaitState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([
      ProjectGrantFormDataDto,
      GeneralGrantFormDataDto,
      GeneralGrantPaperFormDataDto
    ]),
    ...signingData,
    ...sentData,
    ...reviewData,
    ...approvedDecisionData,
    ...maybeApprovedPreReportDecisionData
  },
  'PaymentWaitAppDto'
)
export type PaymentWaitAppDto = t.TypeOf<typeof PaymentWaitAppDto>

export const PreReportAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: PreReportState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([
      ProjectGrantFormDataDto,
      GeneralGrantFormDataDto,
      GeneralGrantPaperFormDataDto
    ]),
    ...signingData,
    ...sentData,
    ...reviewData,
    ...preReportDecisionData
  },
  'PreReportAppDto'
)
export type PreReportAppDto = t.TypeOf<typeof PreReportAppDto>

export const RejectedAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: RejectedState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([
      ProjectGrantFormDataDto,
      GeneralGrantFormDataDto,
      GeneralGrantPaperFormDataDto
    ]),
    ...signingData,
    ...sentData,
    ...reviewData,
    ...preReportOrRejectedDecisionData,
    ...maybeRejectedPreReportDecisionData
  },
  'RejectedApp'
)
export type RejectedAppDto = t.TypeOf<typeof RejectedAppDto>

export const PaidAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: PaidState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([
      ProjectGrantFormDataDto,
      GeneralGrantFormDataDto,
      GeneralGrantPaperFormDataDto
    ]),
    ...signingData,
    ...sentData,
    ...reviewData,
    ...approvedDecisionData,
    ...maybeApprovedPreReportDecisionData,
    paidAt: tv.dateTimeFromString,
    ...maybeApprovedPostReportDecisionData
  },
  'PaidAppDto'
)
export type PaidAppDto = t.TypeOf<typeof PaidAppDto>

export const PostReportAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: PostReportState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([
      ProjectGrantFormDataDto,
      GeneralGrantFormDataDto,
      GeneralGrantPaperFormDataDto
    ]),
    ...signingData,
    ...sentData,
    ...reviewData,
    ...postReportDecisionData,
    paidAt: tv.dateTimeFromString
  },
  'PostReportAppDto'
)
export type PostReportAppDto = t.TypeOf<typeof PostReportAppDto>

export const RecoveryAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: RecoveryState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([
      ProjectGrantFormDataDto,
      GeneralGrantFormDataDto,
      GeneralGrantPaperFormDataDto
    ]),
    ...signingData,
    ...sentData,
    ...reviewData,
    ...approvedDecisionData,
    paidAt: tv.dateTimeFromString,
    ...rejectedPostReportDecisionData
  },
  'RecoveryAppDto'
)
export type RecoveryAppDto = t.TypeOf<typeof RecoveryAppDto>

export const RecoveredAppDto = t.type(
  {
    id: t.string,
    grantPeriodId: t.string,
    state: RecoveredState,
    applicant: t.type({
      ...applicant
    }),
    form: t.union([
      ProjectGrantFormDataDto,
      GeneralGrantFormDataDto,
      GeneralGrantPaperFormDataDto
    ]),
    ...signingData,
    ...sentData,
    ...reviewData,
    ...approvedDecisionData,
    paidAt: tv.dateTimeFromString,
    ...rejectedPostReportDecisionData
  },
  'RecoveredAppDto'
)
export type RecoveredAppDto = t.TypeOf<typeof RecoveredAppDto>

export const AppDto = t.union([
  DraftAppDto,
  SigningAppDto,
  SentAppDto,
  ReviewAppDto,
  PaymentWaitAppDto,
  PreReportAppDto,
  RejectedAppDto,
  PaidAppDto,
  PostReportAppDto,
  RecoveryAppDto,
  RecoveredAppDto
])
export type AppDto = t.TypeOf<typeof AppDto>
